import cx from 'clsx';
import React from 'react';

import { DeviceContext } from '@/components/DeviceProvider/DeviceProvider';
import { HeaderItem } from '@/components/Header/types';
import { MEDIUM_BREAKPOINT } from '@/components/modular-grid/constants';
import { GameClickProps } from '@/components/modular-grid/types';
import HeaderNavigation from '@/components/Navigation/HeaderNavigation';
import { mapNavItems } from '@/components/Navigation/utils';
import { Card } from '@/components/NavigationContent';
import SideNavigation from '@/components/SideNavigation';
import {
  CardCategoryItem,
  NavigationCategory,
  NavigationCategoryItem,
} from '@/components/SideNavigation/types';
import config from '@/config';
import { useWindowSize } from '@/hooks/useWindowSize';
import { DeviceOs } from '@/logic/main/types';
import { AccountLinkType } from '@/types/common';
import { isEqual } from '@/utils/lodash-alternatives';

import styles from './Navigation.module.scss';

interface NavigationPropTypes {
  categories: NavigationCategory[];
  currentPagePath?: string;
  onMenuItemClick?: (args: { path: string; itemTitle: string }) => void;
  onItemClick?: (args: {
    itemTitle?: string;
    path?: string;
    account?: AccountLinkType;
  }) => void;
  onNavigationGameClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationGameInfoClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationCommandClick?: (args: {
    itemTitle: string;
    account?: string;
  }) => void;
  initialLoad?: boolean;
}

const renderResponsiveNavigation = ({
  items,
  onMenuItemClick,
  onItemClick,
  currentPagePath,
  onNavigationCommandClick,
  onNavigationGameClick,
  onNavigationGameInfoClick,
  width,
  mode,
  deviceOs,
  index,
  initialLoad,
}: {
  items: HeaderItem;
  onMenuItemClick: NavigationPropTypes['onMenuItemClick'];
  onItemClick: NavigationPropTypes['onItemClick'];
  onNavigationCommandClick: NavigationPropTypes['onNavigationCommandClick'];
  onNavigationGameClick: NavigationPropTypes['onNavigationGameClick'];
  onNavigationGameInfoClick: NavigationPropTypes['onNavigationGameInfoClick'];
  currentPagePath?: string;
  width: number;
  mode: 'small' | 'default' | 'wide';
  deviceOs: DeviceOs;
  index: number;
  initialLoad?: boolean;
}) => {
  const mappedItems = mapNavItems(items, [currentPagePath], {
    mode,
    width,
    deviceOs,
  });
  const handleMenuClick = (menuItem: { title: string; route_link: string }) => {
    onMenuItemClick &&
      onMenuItemClick({ path: menuItem.route_link, itemTitle: menuItem.title });
  };

  const SmallNavCmp = (
    <HeaderNavigation
      key={index}
      items={mappedItems}
      onItemClick={handleMenuClick}
      onMenuItemClick={onMenuItemClick}
      onNavigationCommandClick={onNavigationCommandClick}
      onNavigationGameClick={onNavigationGameClick}
      onNavigationGameInfoClick={onNavigationGameInfoClick}
      currentPagePath={currentPagePath || '/'}
      initialLoad={initialLoad}
    />
  );
  const LargeNavCmp = (
    <SideNavigation
      key={index}
      categories={[
        {
          type: 'Navigation - Left',
          items: mappedItems,
        },
      ]}
      onItemClick={onItemClick}
      currentPagePath={currentPagePath || '/'}
      onNavigationCommandClick={onNavigationCommandClick}
      onNavigationGameClick={onNavigationGameClick}
      onNavigationGameInfoClick={onNavigationGameInfoClick}
      visible={true}
    />
  );

  const ResponsiveNavCmp =
    width > MEDIUM_BREAKPOINT.max ? LargeNavCmp : SmallNavCmp;

  return (
    <React.Fragment key={'Navigation'}>
      {mode === 'default'
        ? ResponsiveNavCmp
        : mode === 'small'
        ? SmallNavCmp
        : LargeNavCmp}
    </React.Fragment>
  );
};

const renderNavigationItems = (
  category: any,
  mode: 'default' | 'small' | 'wide',
  width: number,
  deviceOs: DeviceOs,
  index: number,
  currentPagePath?: string,
  onMenuItemClick?: (args: { path: string; itemTitle: string }) => void,
  onItemClick?: (args: {
    itemTitle?: string;
    path?: string;
    account?: AccountLinkType;
  }) => void,
  onNavigationCommandClick?: NavigationPropTypes['onNavigationCommandClick'],
  onNavigationGameClick?: NavigationPropTypes['onNavigationGameClick'],
  onNavigationGameInfoClick?: NavigationPropTypes['onNavigationGameInfoClick'],
  initialLoad?: boolean
) => {
  const { items } = category;
  let filteredItems = items;
  switch (mode) {
    case 'default': {
      filteredItems = items.filter((item: HeaderItem) =>
        width > MEDIUM_BREAKPOINT.max
          ? !item.hide_on?.includes('Large')
          : !item.hide_on?.includes('Small')
      );
      break;
    }
    case 'small': {
      filteredItems = items.filter(
        (item: HeaderItem) => !item.hide_on?.includes('Small')
      );
      break;
    }
    case 'wide': {
      filteredItems = items.filter(
        (item: HeaderItem) => !item.hide_on?.includes('Small')
      );
      break;
    }
  }

  switch (category.theme) {
    case 'Card': {
      const handleCardItemClick = (
        event: React.MouseEvent<{}>,
        item: NavigationCategoryItem | CardCategoryItem
      ) => {
        const { route_link, command, title, tracking_metadata } = item;
        if (route_link || command) {
          event.preventDefault();
        }

        onItemClick &&
          onItemClick({
            itemTitle: tracking_metadata?.title || title,
            path: route_link,
            account: command,
          });
      };
      return (
        <Card
          key={'NavigationCard'}
          items={filteredItems}
          handleItemClick={handleCardItemClick}
          title={category.display_title}
          displayList={
            mode === 'small' ||
            (mode === 'default' && width > MEDIUM_BREAKPOINT.max)
          }
        />
      );
    }
    case 'Navigation - Top': {
      return renderResponsiveNavigation({
        items: filteredItems,
        onMenuItemClick,
        onItemClick,
        onNavigationCommandClick,
        onNavigationGameClick,
        onNavigationGameInfoClick,
        width,
        mode,
        deviceOs,
        currentPagePath,
        index,
        initialLoad,
      });
    }
    default:
      return null;
  }
};

const Navigation: React.FC<NavigationPropTypes> = React.memo(
  props => {
    const {
      categories,
      currentPagePath,
      onMenuItemClick,
      onNavigationGameClick,
      onNavigationGameInfoClick,
      onNavigationCommandClick,
      onItemClick,
    } = props;
    const mode = config.staticComponents['Navigation']?.mode || 'default';
    const width = useWindowSize().width;
    const { deviceOs } = React.useContext(DeviceContext);

    return (
      <div
        id="navigation-new"
        className={cx(mode === 'small' ? styles.small : styles.responsive)}
      >
        {categories.map((category, i) =>
          renderNavigationItems(
            category,
            mode,
            width,
            deviceOs,
            i,
            currentPagePath,
            onMenuItemClick,
            onItemClick,
            onNavigationCommandClick,
            onNavigationGameClick,
            onNavigationGameInfoClick,
            !props.currentPagePath
          )
        )}
      </div>
    );
  },
  (props, newProps) => isEqual(props, newProps)
);

export default Navigation;
